import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Button, EMPHASIS, tokensForThemeProvider} from '@forecast-it/design-system';
import {D} from './design-tokens';
import {StatusPill} from './StatusPill';
import {ActionPhase} from './actionCardTypes';
import type {ReportTaxonomy} from '../ai-report-builder/contracts/report-taxonomy.types';
import type {Condition} from '../ai-report-builder/contracts/report-definition.types';
import {formatConditionSummary, SummaryLabels} from '../ai-report-builder/conditions/formatConditionSummary';
import {GROUPING_TOKEN_LABEL_KEY} from '../ai-report-builder/GroupByControl';
import type {GroupingToken} from '../ai-report-builder/ReportTable/useClientGrouping';

const {font, color} = tokensForThemeProvider.sky.sys;
const button = tokensForThemeProvider.sky.comp.button;

/** Confirm card for an agent-proposed report build. The agent no longer opens the builder directly;
 * it proposes the report here (mirroring the write-action HITL cards) and the user confirms before
 * anything opens. On confirm the card settles to a Done state exposing a View action that opens the
 * builder. Chrome intentionally mirrors `ActionCard` so report proposals read like other HITL
 * actions. */

const Card = styled.div`
	border: 1px solid ${D.assistantBubbleBorder};
	border-radius: 8px;
	overflow: hidden;
	margin-top: 8px;
`;
const Body = styled.div`
	background: ${D.assistantBubble};
	padding: 16px;
`;
const Header = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	gap: 8px;
`;
const HeaderTitle = styled.div`
	${font.heading['4']}
	color: ${color.text.content.main};
`;
const ReportBox = styled.div`
	border: 1px solid ${D.assistantBubbleBorder};
	border-left: 3px solid ${D.v2.accent};
	border-radius: 4px;
	padding: 12px;
	background: ${D.bg};
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
`;
const ReportBoxContent = styled.div`
	min-width: 0;
`;
const ReportName = styled.div`
	${font.subheading['2']}
	color: ${color.text.content.main};
`;
/** Confirmed-state open affordance: a plain-text blue link inside the report box (not a filled
 * footer button), matching the other settled HITL cards. A real <button> so it stays keyboard-
 * and screen-reader-accessible despite the link styling. */
const ViewLink = styled.button`
	flex-shrink: 0;
	border: none;
	background: none;
	padding: 0;
	${button.font.standard}
	color: ${button.color.text.lowemphasis.default};
	cursor: pointer;
	&:hover {
		text-decoration: underline;
	}
`;
const ReportSummary = styled.p`
	margin: 4px 0 0;
	${font.body['2']}
	color: ${color.text.content.main};
`;
/** Agent-emitted warnings (out-of-scope terms it dropped, filter values it couldn't resolve),
 * listed under the report box so the user sees what was excluded/unmatched before confirming. */
const FlagList = styled.ul`
	list-style: none;
	margin: 8px 0 0;
	padding: 0;
`;
const FlagItem = styled.li`
	${font.body['2']}
	color: ${D.v2.textSecondary};
	margin-top: 4px;
`;
const Footer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 16px;
	align-items: center;
	padding: 12px 16px;
	background: ${D.bg};
	border-top: 1px solid ${D.assistantBubbleBorder};
`;
const Cancelled = styled.p`
	margin: 0 0 12px;
	font-size: 14px;
	line-height: 20px;
	color: ${D.v2.textSecondary};
`;

export type ReportIntentStatus = 'pending' | 'confirmed' | 'cancelled';

export interface ReportIntentCardProps {
	payload: Record<string, unknown>;
	status: ReportIntentStatus;
	taxonomy?: ReportTaxonomy;
	onConfirm: () => void;
	onCancel: () => void;
	onView: () => void;
}

export function ReportIntentCard({payload, status, taxonomy, onConfirm, onCancel, onView}: ReportIntentCardProps): JSX.Element {
	const {formatMessage} = useIntl();

	const name =
		typeof payload.suggestedName === 'string' && payload.suggestedName.trim()
			? payload.suggestedName
			: formatMessage({id: 'forecast_agent.report_card.default_name', defaultMessage: 'New task report'});
	// One-line description shown beneath the name. Preferred source: the backend's `summaries`
	// (objects `{conditionId, field, text}` from report_builder nodes) — these are the agent's own
	// human-written summary and read correctly even for id-backed or enum-backed fields. Falls back
	// to a taxonomy-built summary (field labels/operator labels/value formatting over the report's
	// FILTER conditions) only when the agent sent no usable summaries; that taxonomy path has no
	// access to a label resolver, so it renders raw ids/enum values verbatim and is a lesser
	// fallback, not the preferred source. A report with neither (e.g. "all tasks grouped by
	// project", no filter) falls back further to the original natural-language prompt so the card
	// always shows what the report is. A bare string element in `summaries` is tolerated for
	// forward-compatibility.
	const labels: SummaryLabels = {
		or: formatMessage({id: 'ai_report_builder.conditions.summary_or', defaultMessage: 'or'}),
		selected: n =>
			formatMessage(
				{id: 'ai_report_builder.conditions.summary_selected', defaultMessage: '{count} selected'},
				{count: n}
			),
		yes: formatMessage({id: 'common.yes', defaultMessage: 'Yes'}),
		no: formatMessage({id: 'common.no', defaultMessage: 'No'}),
	};

	const definition = payload.definition as {filter?: {filterConditions?: Condition[]}} | undefined;
	const filterConditions = definition?.filter?.filterConditions;
	const summaryFromConditions = Array.isArray(payload.summaries)
		? payload.summaries
				.map(s => {
					if (typeof s === 'string') return s;
					if (s && typeof s === 'object' && typeof (s as {text?: unknown}).text === 'string') {
						return (s as {text: string}).text;
					}
					return '';
				})
				.filter(part => part.trim().length > 0)
				.join(' ')
		: '';
	const summaryFromTaxonomy =
		taxonomy && Array.isArray(filterConditions) && filterConditions.length > 0
			? (() => {
					try {
						return filterConditions.map(c => formatConditionSummary(c, taxonomy, {labels})).join('; ');
					} catch {
						return '';
					}
			  })()
			: '';
	const promptText = typeof payload.prompt === 'string' ? payload.prompt.trim() : '';
	const summaryText = summaryFromConditions || summaryFromTaxonomy || promptText;

	// Grouping line, rendered from the definition directly (the agent's `summaries` only ever describe
	// filter conditions, never grouping). Shown alongside the condition summary so a refinement that
	// adds a filter never makes it look like grouping was dropped. Unknown tokens fall through the
	// label lookup and are skipped rather than rendered raw.
	const groupingTokens = (payload.definition as {grouping?: unknown} | undefined)?.grouping;
	const groupLabels = Array.isArray(groupingTokens)
		? groupingTokens
				.filter((t): t is GroupingToken => typeof t === 'string' && t in GROUPING_TOKEN_LABEL_KEY)
				.map(token => formatMessage({id: GROUPING_TOKEN_LABEL_KEY[token]}))
		: [];
	const groupingText = groupLabels.length
		? formatMessage(
				{id: 'ai_report_builder.report_card.grouped_by', defaultMessage: 'Grouped by {groups}'},
				{groups: groupLabels.join(', ')}
		  )
		: '';

	// Agent warnings: out-of-scope terms it removed and filter values it couldn't resolve (each
	// carries a human `message`; unresolved ones may also carry ranked `candidates`).
	const flags = Array.isArray(payload.flags) ? payload.flags : [];

	if (status === 'cancelled') {
		return (
			<Cancelled data-testid="report-intent-cancelled">
				{formatMessage({id: 'forecast_agent.action_card.cancelled', defaultMessage: 'Cancelled.'})}
			</Cancelled>
		);
	}

	const confirmed = status === 'confirmed';

	return (
		<Card data-cy="report-intent-card">
			<Body>
				<Header>
					<HeaderTitle>
						{formatMessage({id: 'forecast_agent.report_card.title', defaultMessage: 'Build new task report'})}
					</HeaderTitle>
					{confirmed && <StatusPill phase={ActionPhase.Done} />}
				</Header>
				<ReportBox>
					<ReportBoxContent>
						<ReportName>{name}</ReportName>
						{groupingText ? <ReportSummary data-cy="report-intent-grouping">{groupingText}</ReportSummary> : null}
						{summaryText ? <ReportSummary>{summaryText}</ReportSummary> : null}
					</ReportBoxContent>
					{confirmed && (
						<ViewLink type="button" onClick={onView} data-cy="report-intent-view">
							{formatMessage({id: 'forecast_agent.report_card.view', defaultMessage: 'View'})}
						</ViewLink>
					)}
				</ReportBox>
				{flags.length > 0 && (
					<FlagList data-testid="report-intent-flags" data-cy="report-intent-flags">
						{flags.map((flag, index) => {
							const record = (flag && typeof flag === 'object' ? flag : {}) as {
								message?: unknown;
								term?: unknown;
								candidates?: unknown;
							};
							const message =
								typeof record.message === 'string' && record.message.trim()
									? record.message.trim()
									: typeof record.term === 'string'
									? record.term.trim()
									: '';
							if (!message) {
								return null;
							}
							const candidates = Array.isArray(record.candidates)
								? record.candidates.filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
								: [];
							const suffix =
								candidates.length > 0
									? ' ' +
									  formatMessage(
											{
												id: 'ai_report_builder.report_card.did_you_mean',
												defaultMessage: 'Did you mean: {candidates}?',
											},
											{candidates: candidates.join(', ')}
									  )
									: '';
							return (
								<FlagItem key={index}>
									{message}
									{suffix}
								</FlagItem>
							);
						})}
					</FlagList>
				)}
			</Body>
			{!confirmed && (
				<Footer>
					<Button emphasis={EMPHASIS.LOW} onPress={onCancel}>
						{formatMessage({id: 'forecast_agent.action_card.cancel', defaultMessage: 'Cancel'})}
					</Button>
					<Button emphasis={EMPHASIS.HIGH} icon={'checkCircle'} onPress={onConfirm} data-cy="report-intent-confirm">
						{formatMessage({id: 'forecast_agent.action_card.confirm', defaultMessage: 'Confirm Action'})}
					</Button>
				</Footer>
			)}
		</Card>
	);
}
